import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

interface Address {
  id: string;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  pinCode: string;
  blockCode: string;
  pamCode: string;
  latitude: number;
  longitude: number;
  addressType: string;
}

const EditAddress = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // The formData will be sent to the API
  const [formData, setFormData] = useState({
    addressLine1: "",
    addressLine2: "",
    addressLine3: "",
    pamCode: "",
    addressType: "",
  });

  // Manage the dropdown selection separately from the custom text
  const [selectedAddressType, setSelectedAddressType] = useState("home");
  const [customAddressType, setCustomAddressType] = useState("");

  // Other states
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [userName, setUserName] = useState("");
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [addressDetails, setAddressDetails] = useState<Address | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch the address details when the component mounts
  useEffect(() => {
    if (id) {
      fetchAddressDetails(id);
    }
  }, [id]);

  // Compare current form data with loaded data to see if anything has changed
  useEffect(() => {
    if (addressDetails) {
      const hasChanges =
        formData.addressLine1 !== addressDetails.addressLine1 ||
        formData.addressLine2 !== addressDetails.addressLine2 ||
        formData.addressLine3 !== addressDetails.addressLine3 ||
        formData.pamCode !== addressDetails.pamCode ||
        formData.addressType !== addressDetails.addressType;
      setIsFormChanged(hasChanges);
    }
  }, [formData, addressDetails]);

  // Load the user name from localStorage
  useEffect(() => {
    const storedUserName = localStorage.getItem("userName");
    if (storedUserName) {
      setUserName(storedUserName);
    }
    const handleStorageChange = () => {
      const updatedUserName = localStorage.getItem("userName");
      if (updatedUserName) {
        setUserName(updatedUserName);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // Fetch address details from the API
  const fetchAddressDetails = async (addressId: string) => {
    setLoading(true);
    const token = localStorage.getItem("token");
    if (!token) {
      console.error("Token not found in localStorage");
      setErrorMessage("Session expired. Please log in again.");
      navigate("/");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `https://pamsapi.vofoxsolutions.com/api/Address/ViewAddedAddressByIdAsync?id=${addressId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch address details");

      const data: Address[] = await response.json(); // Expecting an array of addresses
      if (data.length > 0) {
        const loadedAddressType = data[0].addressType || "";
        // If the loaded addressType is not one of the predefined ones,
        // treat it as custom.
        if (
          loadedAddressType &&
          !["home", "office", "business"].includes(
            loadedAddressType.toLowerCase()
          )
        ) {
          setSelectedAddressType("custom");
          setCustomAddressType(loadedAddressType);
        } else {
          setSelectedAddressType(loadedAddressType);
        }
        setFormData({
          addressLine1: data[0].addressLine1 || "",
          addressLine2: data[0].addressLine2 || "",
          addressLine3: data[0].addressLine3 || "",
          pamCode: data[0].pamCode || "",
          addressType: loadedAddressType || "",
        });
        setAddressDetails(data[0]);
      } else {
        setErrorMessage("No address found.");
      }
    } catch (error) {
      console.error(error);
      setErrorMessage("An error occurred while fetching the address.");
    } finally {
      setLoading(false);
      setIsLoading(false);
    }
  };

  // Handle changes in the text inputs (other than address type)
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle changes in the address type dropdown
  const handleAddressTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedAddressType(value);
    if (value === "custom") {
      // When "Other (Type your own)" is selected,
      // clear the custom value so the user can enter a new one.
      setCustomAddressType("");
      setFormData((prevData) => ({
        ...prevData,
        addressType: "",
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        addressType: value,
      }));
    }
  };

  // Handle changes in the custom address type input
  const handleCustomAddressTypeChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCustomAddressType(e.target.value);
    setFormData((prevData) => ({
      ...prevData,
      addressType: e.target.value,
    }));
  };

  // Update the address via the API
  const handleUpdate = async () => {
    if (!formData.addressLine1 || !formData.addressLine2 || !formData.addressLine3) {
      setErrorMessage("Please fill in all address fields.");
      return;
    }

    setIsSubmitting(true);
    const token = localStorage.getItem("token");
    if (!token) {
      console.error("Token not found in localStorage");
      setErrorMessage("Session expired. Please log in again.");
      navigate("/");
      return;
    }

    try {
      const response = await fetch(
        `https://pamsapi.vofoxsolutions.com/api/address/UpdateAddressAsync?id=${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          // If a custom type is selected, formData.addressType holds the custom text;
          // otherwise it holds one of the predefined types.
          body: JSON.stringify({
            addressLine1: formData.addressLine1,
            addressLine2: formData.addressLine2,
            addressLine3: formData.addressLine3,
            addressType: formData.addressType,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Failed to update address: ${errorData.message}`);
      }

      setTimeout(() => {
        navigate(`/address-details/${id}`);
      }, 2000);
    } catch (error: any) {
      console.error(error);
      setErrorMessage(
        `An error occurred while updating the address: ${error.message}`
      );
      setIsSubmitting(false);
    }
  };

  // Dropdown toggle functions for the header menu
  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const fetchUserProfile = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setErrorMessage("Session expired. Please log in again.");
      navigate("/home");
      return;
    }

    try {
      const response = await fetch(
        "https://pamsapi.vofoxsolutions.com/api/User/GetUserProfile",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch user profile: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();
      setUserName(data.userName);

      navigate("/profile", {
        state: {
          userName: data.userName,
          email: data.email,
          phoneNumber: data.phoneNumber,
        },
      });
    } catch (error) {
      console.error("An error occurred while fetching user profile:", error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("userToken");
    sessionStorage.clear();
    navigate("/");
  };

  // A skeleton loader for the Edit Address form
  const EditAddressSkeleton = () => (
    <div className="w-full p-4 sm:p-8 bg-white rounded-lg shadow-md animate-pulse">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <div className="h-4 w-32 bg-gray-200 rounded mb-2"></div>
          <div className="h-10 w-full bg-gray-200 rounded"></div>
        </div>
        <div>
          <div className="h-4 w-20 bg-gray-200 rounded mb-2"></div>
          <div className="h-10 w-full bg-gray-200 rounded"></div>
        </div>
        <div>
          <div className="h-4 w-16 bg-gray-200 rounded mb-2"></div>
          <div className="h-10 w-full bg-gray-200 rounded"></div>
        </div>
        <div>
          <div className="h-4 w-24 bg-gray-200 rounded mb-2"></div>
          <div className="h-10 w-full bg-gray-200 rounded"></div>
        </div>
        <div className="col-span-1">
          <div className="h-4 w-40 bg-gray-200 rounded mb-2"></div>
          <div className="h-10 w-full bg-gray-200 rounded"></div>
        </div>
        <div className="col-span-1 lg:col-span-4 flex items-center justify-end gap-4 mt-4">
          <div className="h-10 w-24 bg-gray-200 rounded"></div>
          <div className="h-10 w-24 bg-gray-200 rounded"></div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col min-h-screen bg-gray-100">
      <header className="flex items-center justify-between px-4 sm:px-6 py-3 bg-white shadow">
        <h1 className="text-sm sm:text-lg font-semibold text-gray-700">
         
        </h1>
        <div className="flex items-center gap-3 sm:gap-6">
          <div className="relative">
            <button
              onClick={toggleDropdown}
              className="relative border border-whitesmoke focus:outline-none flex rounded-full h-10 sm:h-12 items-center px-2 sm:px-4 gap-2"
            >
              <div className="w-6 sm:w-8 h-6 sm:h-8 overflow-hidden rounded-full">
                <img
                  className="object-cover w-full h-full"
                  src="https://cdn-icons-png.flaticon.com/128/18573/18573514.png"
                  alt="User Avatar"
                />
              </div>
              <h1 className="text-xs sm:text-sm">
                {userName || "Guest"}
              </h1>
            </button>
            {dropdownOpen && (
              <>
                <div
                  onClick={() => setDropdownOpen(false)}
                  className="fixed inset-0 z-10 w-full h-full"
                ></div>
                <div className="absolute right-0 z-20 w-36 sm:w-48 mt-2 bg-white rounded-md shadow-xl">
                  <button
                    onClick={fetchUserProfile}
                    className="block w-full text-left px-2 sm:px-4 py-2 text-xs sm:text-sm text-gray-700 hover:bg-indigo-600 hover:text-white"
                  >
                    Profile
                  </button>
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-2 sm:px-4 py-2 text-xs sm:text-sm text-gray-700 hover:bg-indigo-600 hover:text-white"
                  >
                    Log out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="p-4 sm:p-6 flex-grow">
        <h1 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">
          Edit Address
        </h1>
        {loading ? (
          <EditAddressSkeleton />
        ) : (
          <div className="w-full p-4 sm:p-8 bg-white rounded-lg shadow-md">
            {errorMessage && (
              <p className="text-red-500 mb-4">{errorMessage}</p>
            )}
            {loading ? (
              <p className="text-center text-gray-600">Loading...</p>
            ) : (
              <form className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label
                    htmlFor="addressLine1"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Building Number/House Number
                  </label>
                  <input
                    type="text"
                    id="addressLine1"
                    name="addressLine1"
                    value={formData.addressLine1}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-200 rounded-md bg-gray-50 text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label
                    htmlFor="addressLine2"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Street
                  </label>
                  <input
                    type="text"
                    id="addressLine2"
                    name="addressLine2"
                    value={formData.addressLine2}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-200 rounded-md bg-gray-50 text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label
                    htmlFor="addressLine3"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    City
                  </label>
                  <input
                    type="text"
                    id="addressLine3"
                    name="addressLine3"
                    value={formData.addressLine3}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-200 rounded-md bg-gray-50 text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label
                    htmlFor="pamCode"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Pam Code
                  </label>
                  <input
                    type="text"
                    id="pamCode"
                    name="pamCode"
                    value={formData.pamCode}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-200 rounded-md bg-gray-50 text-black"
                  />
                </div>
                <div className="col-span-1">
                  <label
                    htmlFor="addressType"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Save this Address as
                  </label>
                  {/* Use the separate state variable for dropdown selection */}
                  <select
                    id="addressType"
                    name="addressType"
                    className="w-full rounded-lg bg-slate-50 py-3 pl-6 pr-10 outline-none focus:border-blue focus-visible:shadow-none"
                    value={selectedAddressType}
                    onChange={handleAddressTypeChange}
                  >
                    <option value="home">Home</option>
                    <option value="office">Office</option>
                    <option value="business">Business</option>
                    <option value="custom">Other (Type your own)</option>
                  </select>
                  {/* Show the custom input field if "Other (Type your own)" is selected */}
                  {selectedAddressType === "custom" && (
                    <input
                      type="text"
                      className="mt-2 w-full rounded-lg bg-slate-50 py-2 pl-6 pr-10 outline-none focus:border-blue focus-visible:shadow-none"
                      value={customAddressType}
                      onChange={handleCustomAddressTypeChange}
                      placeholder="Enter address type"
                    />
                  )}
                </div>

                <div className="col-span-1 lg:col-span-4 flex items-center justify-end gap-4 mt-4">
                  <button
                    type="button"
                    onClick={handleUpdate}
                    disabled={
                      !isFormChanged ||
                      isSubmitting ||
                      (selectedAddressType === "custom" && customAddressType.trim() === "")
                    }
                    className={`py-2 px-4 text-white rounded-md shadow focus:outline-none focus:ring-2 focus:ring-blue-500 ${isFormChanged &&
                        !isSubmitting &&
                        (selectedAddressType !== "custom" || customAddressType.trim() !== "")
                        ? "bg-blue-600 hover:bg-blue-700"
                        : "bg-gray-400 cursor-not-allowed"
                      }`}
                  >
                    {isSubmitting ? "Updating..." : "Update"}
                  </button>

                  <button
                    onClick={() => navigate("/addressmapone")}
                    className="py-2 px-4 bg-gray-500 text-white rounded-md shadow hover:bg-gray-600 focus:outline-none"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </main>

      <footer className="w-full mt-auto">
        <div className="px-4 sm:px-6 py-6 bg-white shadow rounded-md">
          <p className="text-center text-gray-600 text-sm">
          GET ME THERE. © 2024
          </p>
        </div>
      </footer>
    </div>
  );
};

export default EditAddress;
